import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wallet,
  Banknote,
  CreditCard,
  History,
  ChevronRight,
  Loader2,
  CheckCircle,
  AlertCircle,
  Sparkles,
  X,
  Zap,
  ArrowLeft,
  Building2,
  Clock,
  Plus,
  DollarSign
} from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const PrizeWalletPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [taxTracking, setTaxTracking] = useState(null);
  const [connectStatus, setConnectStatus] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [withdrawing, setWithdrawing] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [instantPayout, setInstantPayout] = useState(false);
  const [connectingPayout, setConnectingPayout] = useState(false);
  const [settingUpCard, setSettingUpCard] = useState(false);
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const [addFundsAmount, setAddFundsAmount] = useState('');
  const [addingFunds, setAddingFunds] = useState(false);

  useEffect(() => {
    loadWalletData();
    
    // Check for Stripe Connect return
    const connectResult = searchParams.get('connect');
    if (connectResult === 'success') {
      toast.success('Payout account connected successfully!');
      checkConnectStatus();
    } else if (connectResult === 'refresh') {
      toast.info('Please complete your payout account setup');
      handleConnectPayout();
    }
    
    // Check for payment method setup return
    const setupResult = searchParams.get('setup_success');
    if (setupResult === 'true') {
      toast.success('Payment method added successfully!');
      loadPaymentMethods();
    }
    
    // Check if we should open card setup
    const setupCard = searchParams.get('setup_card');
    if (setupCard === 'true') {
      handleSetupCard();
    }
    
    // Check for deposit return
    const depositResult = searchParams.get('deposit');
    const depositId = searchParams.get('deposit_id');
    if (depositResult === 'success' && depositId) {
      verifyDeposit(depositId);
      // Clear URL params after processing
      window.history.replaceState({}, '', '/wallet');
    } else if (depositResult === 'cancelled') {
      toast.info('Deposit cancelled');
      window.history.replaceState({}, '', '/wallet');
    }
    
    // Check for payment method setup return - clean URL
    if (searchParams.get('setup_success') || searchParams.get('setup_cancelled')) {
      window.history.replaceState({}, '', '/wallet');
    }
  }, [searchParams]);

  const loadWalletData = async () => {
    try {
      setLoading(true);
      const [balanceRes, connectRes, methodsRes] = await Promise.all([
        axios.get(`${API}/tournament/prize-balance`),
        axios.get(`${API}/tournament/connect-status`),
        axios.get(`${API}/tournament/payment-methods`)
      ]);
      
      setBalance(balanceRes.data.balance);
      setTransactions(balanceRes.data.transactions || []);
      setTaxTracking(balanceRes.data.tax_tracking);
      setConnectStatus(connectRes.data);
      setPaymentMethods(methodsRes.data.payment_methods || []);
    } catch (error) {
      console.error('Error loading wallet:', error);
      if (error.response?.status === 401) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const res = await axios.get(`${API}/tournament/payment-methods`);
      setPaymentMethods(res.data.payment_methods || []);
    } catch (error) {
      console.error('Error loading payment methods:', error);
    }
  };

  const handleSetupCard = async () => {
    try {
      setSettingUpCard(true);
      const res = await axios.post(`${API}/tournament/setup-payment-method`, {
        return_url: window.location.origin + '/wallet'
      });
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      console.error('Error setting up card:', error);
      toast.error('Failed to set up payment method');
    } finally {
      setSettingUpCard(false);
    }
  };

  const verifyDeposit = async (depositId) => {
    try {
      const res = await axios.get(`${API}/tournament/verify-deposit/${depositId}`);
      if (res.data.success) {
        if (res.data.already_processed) {
          toast.info('Deposit already processed');
        } else {
          toast.success(`$${(res.data.amount / 100).toFixed(2)} added to your wallet!`);
        }
        loadWalletData();
      }
    } catch (error) {
      console.error('Error verifying deposit:', error);
      toast.error('Failed to verify deposit');
    }
  };

  const handleAddFunds = async () => {
    const amountCents = Math.round(parseFloat(addFundsAmount) * 100);
    
    if (isNaN(amountCents) || amountCents < 500) {
      toast.error('Minimum deposit is $5.00');
      return;
    }
    
    if (amountCents > 50000) {
      toast.error('Maximum deposit is $500.00');
      return;
    }
    
    try {
      setAddingFunds(true);
      const res = await axios.post(`${API}/tournament/add-funds`, {
        amount: amountCents,
        return_url: window.location.origin + '/wallet'
      });
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      console.error('Error adding funds:', error);
      toast.error(error.response?.data?.detail || 'Failed to process deposit');
    } finally {
      setAddingFunds(false);
    }
  };

  const checkConnectStatus = async () => {
    try {
      const res = await axios.get(`${API}/tournament/connect-status`);
      setConnectStatus(res.data);
      if (res.data.payout_enabled) {
        loadWalletData();
      }
    } catch (error) {
      console.error('Error checking connect status:', error);
    }
  };

  const handleConnectPayout = async () => {
    try {
      setConnectingPayout(true);
      
      // First check if rules accepted
      const rulesRes = await axios.get(`${API}/tournament/rules-status`);
      if (!rulesRes.data.rules_accepted) {
        toast.info('Please accept tournament rules first');
        navigate('/tournament-onboarding');
        return;
      }
      
      // Check age verification
      if (!rulesRes.data.adult_verified) {
        toast.info('Please verify your age first');
        navigate('/tournament-onboarding?step=age');
        return;
      }
      
      const res = await axios.post(`${API}/tournament/connect-payout`);
      
      if (res.data.already_connected) {
        toast.info('Payout account already connected');
        checkConnectStatus();
      } else if (res.data.onboarding_url) {
        window.location.href = res.data.onboarding_url;
      }
    } catch (error) {
      console.error('Error connecting payout:', error);
      toast.error(error.response?.data?.detail || 'Failed to set up payout account');
    } finally {
      setConnectingPayout(false);
    }
  };

  const handleWithdraw = async () => {
    const amountCents = Math.round(parseFloat(withdrawAmount) * 100);
    
    if (isNaN(amountCents) || amountCents < 1000) {
      toast.error('Minimum withdrawal is $10.00');
      return;
    }
    
    if (amountCents > (balance?.available_balance || 0)) {
      toast.error('Insufficient balance');
      return;
    }
    
    try {
      setWithdrawing(true);
      const res = await axios.post(`${API}/tournament/withdraw`, {
        amount: amountCents,
        instant: instantPayout
      });
      
      if (res.data.success) {
        toast.success(`Withdrawal of $${(res.data.net_amount / 100).toFixed(2)} initiated!`);
        setShowWithdrawModal(false);
        setWithdrawAmount('');
        loadWalletData();
      }
    } catch (error) {
      console.error('Error withdrawing:', error);
      toast.error(error.response?.data?.detail || 'Failed to process withdrawal');
    } finally {
      setWithdrawing(false);
    }
  };

  const formatCents = (cents) => {
    if (!cents && cents !== 0) return '$0';
    return `$${Math.floor(cents / 100)}`;
  };

  const formatCentsFull = (cents) => {
    if (!cents && cents !== 0) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Calculate setup progress
  const getSetupProgress = () => {
    let completed = 0;
    const total = 3;
    
    if (balance?.tournament_rules_accepted && balance?.adult_verified) completed++;
    if (connectStatus?.payout_enabled) completed++;
    if (paymentMethods.length > 0) completed++;
    
    return { completed, total };
  };

  const { completed: setupCompleted, total: setupTotal } = getSetupProgress();
  const availableBalance = balance?.available_balance || 0;
  const canWithdraw = availableBalance >= 1000 && connectStatus?.payout_enabled;

  if (loading) {
    return (
      <div className="ps-theme-page ps-wallet-page min-h-screen flex items-center justify-center" data-testid="wallet-loading">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-3" />
          <p className="text-gray-500">Loading wallet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ps-theme-page ps-wallet-page min-h-screen" data-testid="wallet-page">
      {/* Header */}
      <div className="ps-page-surface border-b px-4 py-4 sticky top-0 z-10" data-testid="wallet-header">
        <div className="max-w-md mx-auto flex items-center">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-gray-100 rounded-full">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900 ml-2">Golf Wallet</h1>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6">
        {/* Balance Card */}
        <div className="ps-page-surface rounded-3xl p-8 shadow-sm border text-center mb-6" data-testid="wallet-balance-panel">
          <p className="text-gray-400 text-sm mb-2">Available Balance</p>
          <p className="text-4xl sm:text-6xl font-bold text-gray-900 mb-4 break-all" data-testid="wallet-balance-value">
            {formatCents(availableBalance)}
          </p>
          
          {canWithdraw ? (
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium">
              <Sparkles className="w-4 h-4" />
              Ready to withdraw!
            </div>
          ) : availableBalance > 0 ? (
            <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-full text-sm">
              <Clock className="w-4 h-4" />
              Complete setup to withdraw
            </div>
          ) : (
            <p className="text-gray-400 text-sm">Win tournaments to earn!</p>
          )}
        </div>

        {/* Action Cards */}
        <div className="space-y-3 mb-6">
          {/* Withdraw Button */}
          <button 
            onClick={() => canWithdraw ? setShowWithdrawModal(true) : toast.info('Complete setup to withdraw')}
            disabled={!canWithdraw && availableBalance === 0}
            className={`w-full p-5 rounded-2xl flex items-center justify-between transition group ${
              canWithdraw 
                ? 'bg-emerald-700 ps-on-color hover:bg-emerald-800' 
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="withdraw-btn"
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                canWithdraw ? 'bg-white/20' : 'bg-gray-200'
              }`}>
                <Banknote className="w-6 h-6" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-lg">Withdraw</p>
                <p className={canWithdraw ? 'text-emerald-100 text-sm' : 'text-gray-400 text-sm'}>
                  To your bank account
                </p>
              </div>
            </div>
            <ChevronRight className={`w-6 h-6 transition ${canWithdraw ? 'group-hover:translate-x-1' : ''}`} />
          </button>

          {/* Add Funds Button */}
          <button 
            onClick={() => setShowAddFundsModal(true)}
            className="ps-home-cta ps-on-color w-full p-5 rounded-2xl flex items-center justify-between transition-colors group shadow-sm"
            data-testid="add-funds-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Plus className="w-6 h-6" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-lg">Add Funds</p>
                <p className="text-emerald-100 text-sm">Deposit for tournaments</p>
              </div>
            </div>
            <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition" />
          </button>

          {/* Add Card / Manage Cards */}
          <button 
            onClick={handleSetupCard}
            disabled={settingUpCard}
            className="w-full bg-white p-5 rounded-2xl flex items-center justify-between hover:bg-gray-50 transition group border border-gray-100"
            data-testid="add-card-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-blue-500" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-lg text-gray-900">
                  {paymentMethods.length > 0 ? 'Payment Cards' : 'Add Card'}
                </p>
                <p className="text-gray-500 text-sm">
                  {paymentMethods.length > 0 
                    ? `${paymentMethods.length} card${paymentMethods.length > 1 ? 's' : ''} saved`
                    : 'For tournament entries'
                  }
                </p>
              </div>
            </div>
            {settingUpCard ? (
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            ) : (
              <ChevronRight className="w-6 h-6 text-gray-400 group-hover:translate-x-1 transition" />
            )}
          </button>

          {/* Set Up Payouts (if not connected) */}
          {!connectStatus?.payout_enabled && (
            <button 
              onClick={handleConnectPayout}
              disabled={connectingPayout}
              className="w-full bg-white p-5 rounded-2xl flex items-center justify-between hover:bg-gray-50 transition group border border-gray-100"
              data-testid="setup-payouts-btn"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-amber-500" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-lg text-gray-900">Set Up Payouts</p>
                  <p className="text-gray-500 text-sm">Connect your bank account</p>
                </div>
              </div>
              {connectingPayout ? (
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              ) : (
                <ChevronRight className="w-6 h-6 text-gray-400 group-hover:translate-x-1 transition" />
              )}
            </button>
          )}

          {/* History */}
          <button 
            onClick={() => setShowHistoryModal(true)}
            className="w-full bg-white p-5 rounded-2xl flex items-center justify-between hover:bg-gray-50 transition group border border-gray-100"
            data-testid="history-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                <History className="w-6 h-6 text-purple-500" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-lg text-gray-900">History</p>
                <p className="text-gray-500 text-sm">
                  {transactions.length > 0 ? `${transactions.length} transactions` : 'View transactions'}
                </p>
              </div>
            </div>
            <ChevronRight className="w-6 h-6 text-gray-400 group-hover:translate-x-1 transition" />
          </button>
        </div>

        {/* Setup Progress */}
        {setupCompleted < setupTotal && (
          <div className="bg-white rounded-2xl p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <p className="font-medium text-gray-900">Setup Progress</p>
              <p className="text-emerald-600 font-semibold">{setupCompleted} of {setupTotal}</p>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
              <div 
                className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all duration-500" 
                style={{ width: `${(setupCompleted / setupTotal) * 100}%` }}
              />
            </div>
            <div className="space-y-2">
              <SetupStep 
                done={balance?.tournament_rules_accepted && balance?.adult_verified} 
                label="Age verified & rules accepted"
                action={() => navigate('/tournament-onboarding')}
              />
              <SetupStep 
                done={connectStatus?.payout_enabled} 
                label="Bank account connected"
                action={handleConnectPayout}
              />
              <SetupStep 
                done={paymentMethods.length > 0} 
                label="Payment card added"
                action={handleSetupCard}
              />
            </div>
          </div>
        )}

        {/* All Setup Complete */}
        {setupCompleted === setupTotal && (
          <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-2xl p-5 border border-emerald-100 text-center">
            <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
            <p className="font-semibold text-emerald-800">All Set!</p>
            <p className="text-emerald-600 text-sm">Your wallet is ready for tournaments</p>
          </div>
        )}
      </div>

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 animate-in slide-in-from-bottom" data-testid="wallet-withdraw-dialog">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Withdraw Funds</h2>
              <button onClick={() => setShowWithdrawModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm text-gray-500 mb-2">Amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400">$</span>
                <input
                  type="number"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-10 pr-4 py-4 text-2xl font-semibold border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="withdraw-amount-input"
                />
              </div>
              <p className="text-sm text-gray-400 mt-2">
                Available: {formatCentsFull(availableBalance)} • Min: $10.00
              </p>
            </div>

            {/* Instant Payout Option */}
            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl mb-6 cursor-pointer">
              <input
                type="checkbox"
                checked={instantPayout}
                onChange={(e) => setInstantPayout(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  <span className="font-medium text-gray-900">Instant Payout</span>
                </div>
                <p className="text-sm text-gray-500">1.5% fee • Arrives in minutes</p>
              </div>
            </label>

            <Button
              onClick={handleWithdraw}
              disabled={withdrawing || !withdrawAmount}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-4 text-lg rounded-xl"
              data-testid="confirm-withdraw-btn"
            >
              {withdrawing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                `Withdraw ${withdrawAmount ? `$${parseFloat(withdrawAmount).toFixed(2)}` : ''}`
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Add Funds Modal */}
      {showAddFundsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 animate-in slide-in-from-bottom" data-testid="wallet-add-funds-dialog">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Add Funds</h2>
              <button onClick={() => setShowAddFundsModal(false)} className="p-2 hover:bg-gray-100 rounded-full" aria-label="Close add funds" data-testid="wallet-add-funds-close">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Quick Amount Buttons */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              {[10, 25, 50, 100].map((amt) => (
                <button
                  key={amt}
                  onClick={() => setAddFundsAmount(amt.toString())}
                  data-testid={`wallet-add-amount-${amt}`}
                  className={`py-3 rounded-xl font-semibold transition ${
                    addFundsAmount === amt.toString()
                      ? 'bg-emerald-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  ${amt}
                </button>
              ))}
            </div>
            
            <div className="mb-6">
              <label className="block text-sm text-gray-500 mb-2">Or enter custom amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400">$</span>
                <input
                  type="number"
                  value={addFundsAmount}
                  onChange={(e) => setAddFundsAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-10 pr-4 py-4 text-2xl font-semibold border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="add-funds-amount-input"
                />
              </div>
              <p className="text-sm text-gray-400 mt-2">
                Min: $5.00 • Max: $500.00
              </p>
            </div>

            <Button
              onClick={handleAddFunds}
              disabled={addingFunds || !addFundsAmount}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-4 text-lg rounded-xl"
              data-testid="confirm-add-funds-btn"
            >
              {addingFunds ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Plus className="w-5 h-5" />
                  Add {addFundsAmount ? `$${parseFloat(addFundsAmount).toFixed(2)}` : 'Funds'}
                </span>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[80vh] flex flex-col animate-in slide-in-from-bottom">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">Transaction History</h2>
              <button onClick={() => setShowHistoryModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {transactions.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No transactions yet</p>
                  <p className="text-gray-400 text-sm">Join tournaments to see activity here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map((tx, idx) => (
                    <div key={tx.transaction_id || idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        tx.type === 'prize_won' ? 'bg-emerald-100' :
                        tx.type === 'deposit' ? 'bg-green-100' :
                        tx.type === 'entry_fee' ? 'bg-red-100' :
                        tx.type === 'payout' ? 'bg-blue-100' : 'bg-gray-100'
                      }`}>
                        {tx.type === 'prize_won' ? <Sparkles className="w-5 h-5 text-emerald-600" /> :
                         tx.type === 'deposit' ? <Plus className="w-5 h-5 text-green-600" /> :
                         tx.type === 'entry_fee' ? <CreditCard className="w-5 h-5 text-red-600" /> :
                         tx.type === 'payout' ? <Banknote className="w-5 h-5 text-blue-600" /> :
                         <History className="w-5 h-5 text-gray-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {tx.type === 'prize_won' ? 'Tournament Winnings' :
                           tx.type === 'deposit' ? 'Wallet Deposit' :
                           tx.type === 'entry_fee' ? 'Entry Fee' :
                           tx.type === 'payout' ? 'Withdrawal' : tx.type}
                        </p>
                        <p className="text-sm text-gray-500">{formatDate(tx.created_at)}</p>
                      </div>
                      <p className={`font-semibold ${
                        tx.type === 'prize_won' || tx.type === 'deposit' ? 'text-emerald-600' :
                        tx.type === 'entry_fee' || tx.type === 'payout' ? 'text-gray-900' : 'text-gray-600'
                      }`}>
                        {tx.type === 'prize_won' || tx.type === 'deposit' ? '+' : '-'}{formatCentsFull(tx.amount)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Setup Step Component
const SetupStep = ({ done, label, action }) => (
  <button 
    onClick={!done ? action : undefined}
    className={`flex items-center gap-3 w-full text-left ${!done ? 'cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-1 rounded-lg' : ''}`}
  >
    {done ? (
      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
    ) : (
      <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
    )}
    <span className={done ? 'text-gray-500 line-through' : 'text-gray-700'}>{label}</span>
    {!done && <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />}
  </button>
);

export default PrizeWalletPage;
